import { devLog } from '../../utils/devLog';

import React, { useState } from 'react';
import { useAppContext } from '../../hooks/useAppContext';
import { useToast } from '../common/Toast';
import Modal from '../common/Modal';
import { deleteRole, updateRoleStatus } from '../../utils/api';

const JobItem = ({ job }) => {
  const { updateJob, deleteJob } = useAppContext();
  const { showSuccess, showError } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  const handleView = () => setShowViewModal(true);

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === job.status) return;
    setIsUpdatingStatus(true);
    try {
      const result = await updateRoleStatus({
        roleId: job.roleId || job.id,
        title: job.title,
        description: job.description,
        requirements: job.requirements,
        totalApplications: job.totalApplications || job.cvCount || 0,
        totalPositions: job.totalPositions || 1
      }, newStatus);

      if (result.success) {
        updateJob(job.id, { ...job, status: newStatus });
        showSuccess(`"${job.title}" status updated to ${newStatus}`, 4000);
      } else {
        showError(result.error || 'Failed to update status', 5000);
      }
    } catch (err) {
      showError('An unexpected error occurred.', 5000);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleHardDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteRole(job.roleId || job.id);
      if (result.success) {
        deleteJob(job.id);
        showSuccess(`Job "${job.title}" has been permanently deleted!`, 5000);
      } else {
        showError(result.error || 'Failed to delete job', 6000);
      }
    } catch (err) {
      devLog.error('Error deleting job:', err);
      showError('An unexpected error occurred while deleting the job.', 6000);
    } finally {
      setIsDeleting(false);
      setShowHardDeleteModal(false);
    }
  };

  const statusColor = job.status === 'Open' ? '#48bb78' : job.status === 'On Hold' ? '#f59e0b' : '#f56565';
  const statusBg = job.status === 'Open' ? '#d1fae5' : job.status === 'On Hold' ? '#fef3c7' : '#fee2e2';
  const statusBorder = job.status === 'Open' ? '#48bb78' : job.status === 'On Hold' ? '#f59e0b' : '#f56565';
  const statusText = job.status === 'Open' ? '#276749' : job.status === 'On Hold' ? '#92400e' : '#9b1c1c';

  return (
    <>
      <div className="job-item" style={{
        opacity: job.status === 'Closed' ? 0.75 : 1,
        border: `2px solid ${job.status === 'Closed' ? '#fecaca' : '#e2e8f0'}`,
        borderRadius: '12px',
        padding: '20px 24px',
        background: 'white',
        transition: 'box-shadow 0.2s ease',
      }}>

        {/* Top Row: Title + CV Badge */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
          <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#2d3748', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {job.title}
            {job.status === 'Closed' && <span style={{ fontSize: '0.8rem' }}>🔒</span>}
          </h4>
          <div style={{
            background: 'linear-gradient(45deg, #667eea, #764ba2)',
            color: 'white',
            padding: '4px 14px',
            borderRadius: '20px',
            fontSize: '0.82rem',
            fontWeight: 700,
            whiteSpace: 'nowrap'
          }}>
            {job.cvCount || job.totalApplications || 0} CVs
          </div>
        </div>

        {/* Description */}
        <div style={{ color: '#718096', fontSize: '0.88rem', lineHeight: 1.6, marginBottom: '12px' }}>
          {job.description ? job.description.substring(0, 120) + '...' : 'No description provided'}
        </div>

        {/* Meta Row: Created + Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.8rem', color: '#a0aec0', display: 'flex', alignItems: 'center', gap: '5px' }}>
            🕐 Created: {job.createdAt}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.8rem', color: '#718096', fontWeight: 600 }}>Status:</span>
            <select
              value={job.status || 'Open'}
              onChange={handleStatusChange}
              disabled={isUpdatingStatus}
              style={{
                padding: '3px 10px',
                borderRadius: '6px',
                border: `2px solid ${statusBorder}`,
                background: statusBg,
                color: statusText,
                fontWeight: 600,
                fontSize: '0.8rem',
                cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                outline: 'none'
              }}
            >
              <option value="Open">✅ Open</option>
              <option value="On Hold">⏸️ On Hold</option>
              <option value="Closed">🔒 Closed</option>
            </select>
            {isUpdatingStatus && <span style={{ color: '#667eea', fontSize: '0.75rem' }}>Updating...</span>}
          </div>
        </div>

        {/* Action Row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={handleView}
            className="button button-secondary"
            disabled={isDeleting}
            style={{ padding: '8px 18px', fontSize: '0.85rem', fontWeight: 600 }}
          >
            👁️ View
          </button>
          <button
            onClick={() => setShowHardDeleteModal(true)}
            disabled={isDeleting}
            style={{
              marginLeft: 'auto',
              background: '#fff5f5',
              border: '2px solid #fed7d7',
              color: '#e53e3e',
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              opacity: isDeleting ? 0.6 : 1,
              transition: 'background 0.2s'
            }}
            title="Permanently Delete"
          >
            🗑️
          </button>
        </div>
      </div>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        onConfirm={() => setShowViewModal(false)}
        title={`📋 ${job.title}`}
        message={
          <div>
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: '#2d3748' }}>Description:</strong>
              <div style={{ marginTop: '8px', color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.6 }}>
                {job.description || 'No description provided'}
              </div>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: '#2d3748' }}>Requirements:</strong>
              <div style={{ marginTop: '8px', color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.6 }}>
                {job.requirements ? (
                  typeof job.requirements === 'object' ? (
                    <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                      {Object.entries(job.requirements).map(([key, value]) => (
                        <li key={key} style={{ marginBottom: '5px' }}>
                          <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>{' '}
                          {Array.isArray(value) ? value.join(', ') : value}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{job.requirements}</div>
                  )
                ) : 'No requirements specified'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
              <div>
                <strong style={{ color: '#2d3748' }}>Total Positions:</strong>{' '}
                <span style={{ color: '#4a5568' }}>{job.totalPositions || 1}</span>
              </div>
              <div>
                <strong style={{ color: '#2d3748' }}>Applications:</strong>{' '}
                <span style={{ color: '#4a5568' }}>{job.totalApplications || job.cvCount || 0}</span>
              </div>
              <div>
                <strong style={{ color: '#2d3748' }}>Status:</strong>{' '}
                <span style={{ color: statusColor, fontWeight: 600 }}>{job.status || 'Open'}</span>
              </div>
            </div>
          </div>
        }
        confirmText="Close"
        cancelText=""
        type="primary"
      />

      {/* Hard Delete Confirmation Modal */}
      <Modal
        isOpen={showHardDeleteModal}
        onClose={() => setShowHardDeleteModal(false)}
        onConfirm={handleHardDeleteConfirm}
        title="🗑️ Permanently Delete Job"
        message={
          <div>
            <p>Are you sure you want to <strong>permanently delete</strong> "{job.title}"?</p>
            <p style={{ marginTop: '10px', color: '#e53e3e', fontWeight: 'bold' }}>
              ⚠️ This action cannot be undone!
            </p>
            <ul style={{ marginTop: '10px', paddingLeft: '20px', fontSize: '0.9rem', color: '#4a5568' }}>
              <li>The job role will be deleted.</li>
              <li>All associated CVs/Candidates will be deleted.</li>
              <li>All interview questions for this role will be deleted.</li>
              <li>All analysis data will be deleted.</li>
            </ul>
          </div>
        }
        confirmText="Permanently Delete"
        cancelText="Cancel"
        type="danger"
      />
    </>
  );
};

export default JobItem;
